import { useEffect, useState } from "react";
import {
  Eye,
  MousePointer,
  TrendingUp,
  DollarSign,
  Wallet,
  BarChart3,
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import MetricCard from "../../components/PrebidAds/MetricCard";
import FilterModal from "../../components/PrebidAds/FilterModal";
import DataTable from "../../components/PrebidAds/DataTable";
import {
  partnerData,
  operWarpData,
  calculateTotals,
} from "../../data/mockData";
import Layout from "../../components/Layout/Layout";
import DataTableFirst from "../../components/PrebidAds/DataTableFirst";
import { useSearchParams } from "react-router-dom";

const metricIconMap = {
  revenue: DollarSign,
  monetized_ecpm: DollarSign,
  monetized_impressions: Eye,
  request_count: MousePointer,
  response_count: Eye,
  avg_latency: BarChart3,
  bid_rate: TrendingUp,
  win_rate: TrendingUp,
};

const Prebiddashboard = () => {
  const filterAndFlattenData = (dataArray, dimensions = [], metrics = []) => {
    return dataArray.map((item) => {
      const filteredItem = {};

      // Include only dimensions
      dimensions.forEach((dim) => {
        filteredItem[dim] = item[dim];
      });

      // Include only metrics
      metrics.forEach((metric) => {
        let value = item[metric];

        // Handle $numberDecimal
        if (value && typeof value === "object" && "$numberDecimal" in value) {
          value = parseFloat(value.$numberDecimal);
        }

        filteredItem[metric] = value;
      });

      return filteredItem;
    });
  };

  // open wrap
  const [activeTab, setActiveTab] = useState("partner");
  const [filters, setFilters] = useState({
    dateFrom: undefined,
    dateTo: undefined,
    dimensions: [],
    metrics: [],
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch data whenever the page URL or active tab changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const startDate = params.get("startDate") || "";
    const endDate = params.get("endDate") || "";
    const dimesions = params.get("dimensions")?.split(",") || [];
    const metrics = params.get("metrics")?.split(",") || [];
    const limit = params.get("limit") || 50;
    const page = params.get("page") || 1;
    // Update filters state

    // Skip fetch if dates are missing
    if (!startDate || !endDate) {
      console.warn("Missing startDate or endDate in URL, skipping API fetch");
      setData([]);
      return;
    }

    setLoading(true);

    // Both tabs use get-partner for table data, get-metrics is only for metric cards
    const apiBase =
      `${process.env.REACT_APP_DMP_BASE_URL}api/v1/prebid/get-partner-process`;

    // Build API URL manually to avoid encoding commas
    const apiUrl =
      `${apiBase}?dimesions=${dimesions.join(",")}` +
      `&metrics=${metrics.join(",")}` +
      `&startDate=${startDate}&endDate=${endDate}` +
      `&limit=${limit}&page=${page}`;

    console.log(`Fetching API URL for tab "${activeTab}":`, apiUrl);

    fetch(apiUrl)
      .then((res) => res.json())
      .then((json) => {
        console.log("API response:", json);
        if (json.success && json.data?.result) {
          // Filter the data according to dimensions and metrics
          const filtered = filterAndFlattenData(
            json.data.result,
            dimesions, // dimensions from URL
            metrics, // metrics from URL
          );
          setData(filtered);
          console.log("Filtered API data:", filtered);
        } else {
          setData([]);
          console.warn("API returned no data or unsuccessful response");
        }
      })
      .catch((err) => {
        console.error("API fetch error:", err);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [activeTab, window.location.search]);

  const [metricsData, setMetricsData] = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const startDate = params.get("startDate");
    const endDate = params.get("endDate");

    // Metrics you want on cards
    const metrics = params.get("metrics")?.split(",") || [];

    if (!startDate || !endDate) {
      setMetricsData({});
      return;
    }

    setMetricsLoading(true);

    const metricsApiUrl =
      `${process.env.REACT_APP_DMP_BASE_URL}api/v1/prebid/get-metrics-process` +
      `?metrics=${metrics}&startDate=${startDate}&endDate=${endDate}`;

    fetch(metricsApiUrl)
      .then((res) => res.json())
      .then((json) => {
        if (json.success && json.data) {
          setMetricsData(json.data);
        } else {
          setMetricsData({});
        }
      })
      .catch((err) => {
        console.error("Metrics API error:", err);
        setMetricsData({});
      })
      .finally(() => setMetricsLoading(false));
  }, [window.location.search]);

  // Per-tab metric option sets — partner vs operwarp
  const partnerMetricOptions = [
    { label: "Revenue", value: "revenue" },
    { label: "Monetized Impressions", value: "monetized_impressions" },
    { label: "Monetized eCPM", value: "monetized_ecpm" },
    { label: "Average Latency", value: "avg_latency" },
    { label: "Request Count", value: "request_count" },
    { label: "Response Count", value: "response_count" },
    { label: "Bid Rate", value: "bid_rate" },
    { label: "Win Rate", value: "win_rate" },
    { label: "Timeout Count", value: "timeout_count" },
  ];

  const operWarpMetricOptions = [
    { label: "Revenue", value: "revenue" },
    { label: "Requests", value: "request_count" },
    { label: "Responses", value: "response_count" },
    { label: "Latency (avg)", value: "avg_latency" },
    { label: "Impressions", value: "monetized_impressions" },
    { label: "eCPM", value: "monetized_ecpm" },
    { label: "Bid Rate", value: "bid_rate" },
    { label: "Win Rate", value: "win_rate" },
        { label: "Timeout Count", value: "timeout_count" },

  ];

  // Choose metric options depending on the active tab
  const currentMetricOptions =
    activeTab === "partner" ? partnerMetricOptions : operWarpMetricOptions;

  const metricLabelMap = currentMetricOptions.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});
  const currentData = activeTab === "partner" ? partnerData : operWarpData;
  const metricCount = Object.keys(metricsData).length;

  const formatMetricValue = (value) => {
    // If value is a number, return 2 decimals
    if (typeof value === "number") {
      return value.toFixed(4);
    }

    // If it's a numeric string
    if (!isNaN(value)) {
      return parseFloat(value).toFixed(4);
    }

    // Otherwise return as-is
    return value;
  };
  return (
    <Layout bodynew={true}>
      <div className="min-h-screen bg-background">
        {/* Background subtle gradient */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10">
          {/* Header */}
          <header className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-20">
            <div className="container mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-primary">
                    <BarChart3 className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold gradient-text">
                      Ads Metrics CMS
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Analytics Dashboard
                    </p>
                  </div>
                </div>
                <FilterModal
                  filters={filters}
                  onFiltersChange={setFilters}
                  metricOptions={currentMetricOptions}
                />
              </div>
            </div>
          </header>

          <main className="container mx-auto px-6 py-8">
            {/* Metric Cards */}

            <div
              className="grid gap-4 mb-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              style={{
                gridTemplateColumns: `repeat(${metricCount}, minmax(0, 1fr))`,
              }}
            >
              {Object.entries(metricsData).map(([key, value], index) => {
                const label = metricLabelMap[key];
                const Icon = metricIconMap[key];

                if (!label || !Icon) return null;

                return (
                  <MetricCard
                    key={key}
                    title={label}
                    value={
                      metricsLoading ? "Loading..." : formatMetricValue(value)
                    }
                    icon={Icon}
                    delay={index * 100}
                  />
                );
              })}
            </div>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="space-y-6"
            >
              <TabsList className="bg-secondary/50 border border-border p-1">
                <TabsTrigger
                  value="partner"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all"
                >
                  Partner
                </TabsTrigger>
                <TabsTrigger
                  value="operwarp"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all"
                >
                  Oper Warp
                </TabsTrigger>
              </TabsList>

              <TabsContent value="partner" className="animate-fade-in">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      Partner Performance
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {data.length} partners
                    </p>
                  </div>
                  <DataTableFirst data={data} />
                </div>
              </TabsContent>
              <TabsContent value="operwarp" className="animate-fade-in">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      Oper Warp Campaigns
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {data.length} campaigns
                    </p>
                  </div>
                  <DataTableFirst data={data} />
                </div>
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default Prebiddashboard;
